import styled from '@emotion/styled';
import { TableBody, TableCell, TableRow } from '@material-ui/core';
import _HelpIcon from '@material-ui/icons/HelpOutline';
import { ClassAttributes, createElement, Fragment } from 'react';
import { FormattedMessage } from 'react-intl';

import {
  CreativeFormat,
  CreativeFormatAspectRatio,
  CreativeFormatFastStart,
  CreativeFormatPlaybackTime,
  File,
  FileAttributeType,
} from '../../../../graphql/generated/graphql';
import { subText } from '../../../constant/color';
import { SPACING_UNIT } from '../../../constant/spacing';

interface Props {
  file?:
    | (Pick<File, 'fastStart'> & {
        attribute: Pick<FileAttributeType, 'aspectRatio' | 'playbackTime' | 'volume'>;
      })
    | null;
  creativeFormat: {
    creativeType: Pick<CreativeFormat['creativeType'], 'key' | 'name'>;
    attribute: {
      playbackTime?: {
        seconds: CreativeFormatPlaybackTime['seconds'];
      } | null;
      aspectRatio?: Pick<CreativeFormatAspectRatio, 'aspectRatio' | 'size'> | null;
      fastStart?: CreativeFormatFastStart | null;
    };
  };
  className?: string;
}

type CreativeAttributeRow<T> = {
  creativeFormatData?: T;
  uploadedFileData?: T;
};

type WidthRow = CreativeAttributeRow<number>;
type HeightRow = CreativeAttributeRow<number>;
type SecondsRow = CreativeAttributeRow<number>;

type VolumeRow = {
  uploadedFileData?: number | null;
};

type MoovAtomRow = CreativeAttributeRow<boolean | null>;

function translation(key: string) {
  return `show_creation_page.submission.attribute_table.${key}`;
}

const WidthAttributeRow = (widthRow: WidthRow) => {
  const creativeFormatData = widthRow?.creativeFormatData;
  const uploadedFileData = widthRow?.uploadedFileData;

  return (
    <TableRow>
      <TableCell component="th" scope="row">
        <FormattedMessage id={translation('width')} />
      </TableCell>
      <TableCell>{creativeFormatData ? Math.floor(creativeFormatData) : '-'}</TableCell>
      <TableCell>{uploadedFileData ? Math.floor(uploadedFileData) : '-'}</TableCell>
    </TableRow>
  );
};

const HeightAttributeRow = (heightRow: HeightRow) => {
  const creativeFormatData = heightRow?.creativeFormatData;
  const uploadedFileData = heightRow?.uploadedFileData;

  return (
    <TableRow>
      <TableCell component="th" scope="row">
        <FormattedMessage id={translation('height')} />
      </TableCell>
      <TableCell>{creativeFormatData ? Math.floor(creativeFormatData) : '-'}</TableCell>
      <TableCell>{uploadedFileData ? Math.floor(uploadedFileData) : '-'}</TableCell>
    </TableRow>
  );
};

const SecondsAttributeRow = (secondsRow: SecondsRow) => {
  const formatVideoDuration = (actualDuration: number) => {
    const decimalPlaces = 1;
    return (Math.floor(actualDuration * 10 ** decimalPlaces) / 10 ** decimalPlaces).toFixed(1);
  };
  const creativeFormatData = secondsRow?.creativeFormatData;
  const uploadedFileData = secondsRow?.uploadedFileData;

  return (
    <TableRow>
      <TableCell component="th" scope="row">
        <FormattedMessage id={translation('playback_time')} />
      </TableCell>
      <TableCell>
        {creativeFormatData ? (
          <FormattedMessage id="creative_format.playback_time" values={{ seconds: creativeFormatData }} />
        ) : (
          '-'
        )}
      </TableCell>
      <TableCell>
        {uploadedFileData ? (
          <FormattedMessage
            id="creative_format.playback_time"
            values={{ seconds: formatVideoDuration(uploadedFileData) }}
          />
        ) : (
          '-'
        )}
      </TableCell>
    </TableRow>
  );
};

const VolumeAttributeRow = (volumeRow: VolumeRow) => {
  const uploadedFileData = volumeRow?.uploadedFileData;

  return (
    <TableRow>
      <TableCell component="th" scope="row">
        <FormattedMessage id={translation('max_volume')} />
      </TableCell>
      <TableCell>{'-'}</TableCell>
      <TableCell>
        {uploadedFileData ? (
          <FormattedMessage id="creative_format.max_volume" values={{ max_volume: uploadedFileData }} />
        ) : (
          '-'
        )}
      </TableCell>
    </TableRow>
  );
};

const HelpIcon = styled(_HelpIcon)({
  width: 15,
  height: 15,
  color: subText,
  display: 'inline-flex',
  verticalAlign: 'middle',
  '&:hover': {
    opacity: 0.8,
  },
});

const HelpLink = styled('a')({
  marginLeft: SPACING_UNIT,
});

const MoovAtomNameCell = () => {
  return (
    <Fragment>
      <FormattedMessage id={translation('fast_start')} />
      <HelpLink href="https://help.kaizen-ad.com/ja/articles/3557233" target="_blank" rel="noopener noreferrer">
        <HelpIcon />
      </HelpLink>
    </Fragment>
  );
};

const MoovAtomAttributeRow = (moovAtomRow: MoovAtomRow) => {
  const creativeFormatData = moovAtomRow?.creativeFormatData;
  const uploadedFileData = moovAtomRow?.uploadedFileData;

  return (
    <TableRow>
      <TableCell component="th" scope="row">
        <MoovAtomNameCell />
      </TableCell>
      <TableCell>{creativeFormatData ? <FormattedMessage id="creative_format.fast_start.enabled" /> : '-'}</TableCell>
      <TableCell>
        {uploadedFileData != null ? (
          uploadedFileData ? (
            <FormattedMessage id="creative_format.fast_start.enabled" />
          ) : (
            <FormattedMessage id="creative_format.fast_start.disabled" />
          )
        ) : (
          '-'
        )}
      </TableCell>
    </TableRow>
  );
};

const CreativeAttributeTableRows = ({ file, creativeFormat, className }: ClassAttributes<HTMLElement> & Props) => {
  const creativeFormatAttribute = creativeFormat.attribute;
  const uploadedFileAttribute = file?.attribute;

  const widthRow = {
    creativeFormatData: creativeFormatAttribute.aspectRatio?.size.width,
    uploadedFileData: uploadedFileAttribute?.aspectRatio?.size.width,
  };

  const heightRow = {
    creativeFormatData: creativeFormatAttribute.aspectRatio?.size.height,
    uploadedFileData: uploadedFileAttribute?.aspectRatio?.size.height,
  };

  const secondsRow = {
    creativeFormatData: creativeFormatAttribute.playbackTime?.seconds,
    uploadedFileData: uploadedFileAttribute?.playbackTime?.seconds,
  };

  const volumeRow = {
    uploadedFileData: uploadedFileAttribute?.volume?.maxVolume,
  };

  const moovAtomRow = {
    creativeFormatData: creativeFormatAttribute.fastStart?.enabled,
    uploadedFileData: file?.fastStart?.enabled,
  };

  return (
    <TableBody className={className}>
      <WidthAttributeRow {...widthRow} />
      <HeightAttributeRow {...heightRow} />
      {creativeFormat.creativeType.name === 'Video' && (
        <Fragment>
          <SecondsAttributeRow {...secondsRow} />
          <VolumeAttributeRow {...volumeRow} />
          <MoovAtomAttributeRow {...moovAtomRow} />
        </Fragment>
      )}
    </TableBody>
  );
};

export default CreativeAttributeTableRows;
